/**
 * PricingHeader - Pricing box card for payment page
 *
 * Shows trial pricing in a card format with:
 * - "Early Access Discount" badge at top-left (overlapping border)
 * - "7-day free trial" + "then $X/month" on left, "$0 Today" on right
 * - Small note showing annual billing amount
 */

interface PricingHeaderProps {
  /** Whether this is a trial offer */
  isTrial?: boolean;
  /** Annual price in dollars */
  annualPrice?: number;
  /** Whether pricing is loading */
  isLoading?: boolean;
}

export function PricingHeader({
  isTrial = true,
  annualPrice,
  isLoading = false,
}: PricingHeaderProps) {
  // Calculate monthly equivalent (rounded)
  const monthlyPrice = annualPrice !== undefined ? Math.round(annualPrice / 12) : undefined;

  if (isLoading || annualPrice === undefined) {
    return (
      <div className="relative mt-3 w-full rounded-2xl border-2 border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 animate-pulse rounded bg-slate-200" />
          <div className="h-5 w-20 animate-pulse rounded bg-slate-200" />
        </div>
        <div className="mt-2 h-4 w-48 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  if (isTrial) {
    return (
      <div className="relative mt-3 w-full rounded-2xl border-2 border-slate-200 bg-white px-6 pt-4 pb-4">
        {/* Early Access Discount badge - positioned at top-left, overlapping border */}
        <div className="absolute -top-3 left-6 flex h-5 items-center rounded bg-catch-avocado-400 px-2">
          <span className="text-[10px] font-semibold leading-none text-catch-dark-blue-90">
            Early Access Discount
          </span>
        </div>

        {/* Main pricing row */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-catch-dark-blue-90">
              7-day free trial
            </span>
            <span className="-mt-1 text-base font-bold text-catch-green-dark">
              then ${monthlyPrice}/month
            </span>
            <span className="-mt-0.5 text-xs text-slate-500">
              *billed annually at ${annualPrice}
            </span>
          </div>
          <span className="-mt-3 text-lg font-bold text-catch-green-dark">$0 Today</span>
        </div>
      </div>
    );
  }

  // Non-trial pricing
  return (
    <div className="relative w-full rounded-2xl border-2 border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-catch-dark-blue-90">
          Catch Membership
        </span>
        <span className="text-lg font-bold text-catch-dark-blue-90">
          ${annualPrice}/year
        </span>
      </div>
    </div>
  );
}

export default PricingHeader;
